import { isGuidedDiagnosisDiseaseIcd11Code } from "../consultation/_lib/bacterial-diseases-codes";
import { mapDiseaseToPatientClinicalResource } from "../consultation/_lib/fhir-mapper";
import { InvestigationFormItem } from "../investigations/new/validation-schema";
import { FHIRResourceTypes } from "./clinical-decision-support-service/db/collections/common";
import {
  ALLOWED_RESULT_LOINC_CODES,
  AllResultsToSnomedCode,
  EXTERNAL_CODING_TO_URI_PREFIX,
} from "./clinical-decision-support-service/mappers";
import type { useClinicalDecisionSupportService } from "./clinical-decision-support-service/provider";

const getLoincCode = (property: {
  investigationMeta?: { coding?: { loinc?: { code?: string } } };
}) => property.investigationMeta?.coding?.loinc?.code;

const isAllowedLoincCode = (loincCode: string) =>
  Object.values(ALLOWED_RESULT_LOINC_CODES).includes(loincCode);

const createUri = (loincCode: string) =>
  `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${loincCode}`;

const getOrderedProperties = (catalogueItem: {
  result?: {
    resultSchema?: {
      orderedProperties?: Array<{
        name: string;
        investigationMeta?: { coding?: { loinc?: { code?: string } } };
      }>;
    };
  };
}) => catalogueItem.result?.resultSchema?.orderedProperties || [];

export const addRelevantFormInvestigationsToCDS = ({
  formItem,
  addClinicalResource,
  userId,
}: {
  formItem: InvestigationFormItem;
  addClinicalResource: ReturnType<
    typeof useClinicalDecisionSupportService
  >["patientClinicalResources"]["add"];
  userId: string;
}) => {
  const { catalogueItem, outcome } = formItem;
  const orderedProperties = getOrderedProperties(catalogueItem);

  for (const property of orderedProperties || []) {
    const loincCode = getLoincCode(property);
    const propertyName = property.name;
    if (propertyName !== "notes" && isAllowedLoincCode(loincCode)) {
      const resource = {
        fhirResource: {
          resourceType: FHIRResourceTypes["investigation-activity"],
          externalCoding: {
            loinc: { uri: createUri(loincCode) },
            icd10: null,
            icd11: null,
            snomed: null,
            elephantInternal: null,
          },
          diagnosticReport: {
            result: [] as {
              value: { externalCoding: { snomed: { uri: string } } };
            }[],
          },
        },
        meta: {
          createdAt: new Date().toISOString(),
          createdByUserId: userId,
          updatedAt: new Date().toISOString(),
          updatedByUserId: userId,
        },
      };

      const outcomeValue = outcome?.[propertyName]?.trim().toLowerCase();
      if (
        outcomeValue &&
        Object.keys(AllResultsToSnomedCode).includes(outcomeValue)
      ) {
        resource.fhirResource.diagnosticReport.result.push({
          value: {
            externalCoding: {
              snomed: {
                uri: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}${AllResultsToSnomedCode[outcomeValue]}`,
              },
            },
          },
        });
      }
      addClinicalResource("loinc", resource);
    }
  }
};

export const removeRelevantFormInvestigationsFromCDS = ({
  formItem,
  removeItem,
}: {
  formItem: {
    catalogueItem?: {
      result?: {
        resultSchema?: {
          orderedProperties?: Array<{
            name: string;
            investigationMeta?: { coding?: { loinc?: { code?: string } } };
          }>;
        };
      };
    };
  };
  removeItem: ReturnType<
    typeof useClinicalDecisionSupportService
  >["patientClinicalResources"]["remove"];
}) => {
  const { catalogueItem } = formItem;
  const deDupedLoincCodes = Array.from(
    new Set<string>(
      (getOrderedProperties(catalogueItem) || [])
        .map(getLoincCode)
        .filter(Boolean),
    ),
  );

  for (const loincCode of deDupedLoincCodes) {
    if (isAllowedLoincCode(loincCode)) {
      const uri = createUri(loincCode);
      removeItem(uri);
    }
  }
};

export const addRelevantFormDiagnosesToCDS = ({
  formItem,
  addClinicalResource,
  userId,
}: {
  formItem: any;
  addClinicalResource: ReturnType<
    typeof useClinicalDecisionSupportService
  >["patientClinicalResources"]["add"];
  userId: string;
}) => {
  const {
    codification: { code },
  } = formItem;
  if (!code) return;
  if (!isGuidedDiagnosisDiseaseIcd11Code(code)) return;
  const resource = mapDiseaseToPatientClinicalResource(code, userId);
  addClinicalResource("icd11", resource);
};
